import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { type BackgroundOption, fetchBackgrounds } from "@/src/api/backgrounds";

export type BackgroundSelection = {
  background: string;
  backgroundLabel: string;
  backgroundProfileId: string;
  originFeatId: string;
  relevantAbilities?: string[];
};

type BackgroundStepProps = {
  onNext: (selection: BackgroundSelection) => void;
  onSkip: () => void;
  onBack: () => void;
};

export function BackgroundStep({
  onNext,
  onSkip,
  onBack,
}: BackgroundStepProps) {
  const [backgrounds, setBackgrounds] = useState<BackgroundOption[] | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [selectedBg, setSelectedBg] = useState<BackgroundOption | null>(null);

  useEffect(() => {
    let active = true;
    fetchBackgrounds()
      .then((data) => {
        if (active) {
          setBackgrounds(data);
        }
      })
      .catch((e: unknown) => {
        if (active) {
          setError(e instanceof Error ? e.message : String(e));
        }
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6 px-4 py-8">
      <div className="text-center">
        <h2 className="font-semibold text-xl">Choose your Origin</h2>
        <p className="mt-1 text-muted-foreground text-sm">
          Your origin grants skills and an Origin Feat.
        </p>
      </div>

      <div className="flex w-full max-w-sm flex-col gap-3">
        {error && <div className="p-4 text-red-500">Error: {error}</div>}
        {!backgrounds && !error && (
          <div className="flex justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {backgrounds?.map((bg) => {
          const isSelected = selectedBg?.backgroundId === bg.backgroundId;
          return (
            <div className="flex flex-col gap-3" key={bg.backgroundId}>
              <Card
                className={`cursor-pointer transition-colors ${
                  isSelected ? "border-primary" : "hover:border-primary/50"
                }`}
                onClick={() => {
                  setSelectedBg(bg);
                }}
              >
                <CardHeader>
                  <CardTitle className="text-lg">{bg.label}</CardTitle>
                  <p className="mt-2 text-muted-foreground text-sm leading-relaxed">
                    {bg.description}
                  </p>
                  {bg.equipmentSupport === "deferred" && (
                    <p className="mt-2 text-muted-foreground text-xs">
                      Equipment deferred
                      {bg.equipmentNote ? `: ${bg.equipmentNote}` : ""}
                    </p>
                  )}
                </CardHeader>
              </Card>
            </div>
          );
        })}
      </div>

      <div className="flex flex-col items-center gap-3">
        <div className="flex gap-3">
          <Button onClick={onBack} type="button" variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button
            className="w-36"
            disabled={!selectedBg}
            onClick={() => {
              if (selectedBg) {
                onNext({
                  background: selectedBg.backgroundId,
                  backgroundLabel: selectedBg.label,
                  backgroundProfileId: selectedBg.profileId,
                  originFeatId: selectedBg.originFeatId,
                  relevantAbilities: selectedBg.relevantAbilities,
                });
              }
            }}
            type="button"
          >
            Select
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        <button
          className="text-muted-foreground text-sm underline transition-colors hover:text-foreground"
          onClick={onSkip}
          type="button"
        >
          Skip — no origin
        </button>
      </div>
    </div>
  );
}
